// InvoiceRetention.js
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Paper,
} from "@mui/material";
import TotalForm from "./InvoiceProduct/TotalForm";
import { RET_IR_OPTIONS, RET_IVA_OPTIONS } from "./InvoiceRetention/Retention";

export default function InvoiceRetention({ rows }) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : parseInt(r.cantidad, 10) || 0;
    const price = r.pvp === "" ? 0 : parseFloat(r.pvp) || 0;
    const base = price * qty;

    const mode = r.descuentoModo === "val" ? "val" : "pct";
    const pct = r.descuentoPct === "" ? 0 : parseFloat(r.descuentoPct) || 0;
    const val = r.descuentoValor === "" ? 0 : parseFloat(r.descuentoValor) || 0;

    const descTotal =
      mode === "val"
        ? clamp(val, 0, base)
        : clamp(base * (pct / 100), 0, base);

    const subtotal = base - descTotal;

    const ivaPct = Number(r.iva_porcentaje || 0);
    const ivaVal = ivaPct > 0 ? subtotal * (ivaPct / 100) : 0;

    return { subtotal, ivaVal };
  };

  const mapIR = useMemo(() => {
    const m = new Map();
    (RET_IR_OPTIONS || []).forEach((x) => {
      m.set(String(x.codigo).trim(), x);
    });
    return m;
  }, []);

  const mapIVA = useMemo(() => {
    const m = new Map();
    (RET_IVA_OPTIONS || []).forEach((x) => {
      m.set(String(x.codigo).trim(), x);
    });
    return m;
  }, []);

  const retenciones = useMemo(() => {
    const acc = new Map();

    safeRows.forEach((r) => {
      const { subtotal, ivaVal } = calcRow(r);

      const ir = String(r.ret_ir || "").trim();
      if (ir) {
        const opt = mapIR.get(ir);
        const pct = opt?.porcentaje != null ? Number(opt.porcentaje) : 0;
        const desc = opt?.descripcion || `Código ${ir}`;
        const key = `1|${ir}`;

        const base = subtotal;
        const valor = base * (pct / 100);

        if (!acc.has(key)) {
          acc.set(key, {
            tipo: "1",
            codigo_retencion: ir,
            descripcion: desc,
            porcentaje: pct,
            base: 0,
            valor: 0,
          });
        }

        const it = acc.get(key);
        it.base += base;
        it.valor += valor;
      }

      const iva = String(r.ret_iva || "").trim();
      if (iva) {
        const opt = mapIVA.get(iva);
        const pct = opt?.porcentaje != null ? Number(opt.porcentaje) : 0;
        const desc = opt?.descripcion || `Código ${iva}`;
        const key = `2|${iva}`;

        const base = ivaVal;
        const valor = base * (pct / 100);

        if (!acc.has(key)) {
          acc.set(key, {
            tipo: "2",
            codigo_retencion: iva,
            descripcion: desc,
            porcentaje: pct,
            base: 0,
            valor: 0,
          });
        }

        const it = acc.get(key);
        it.base += base;
        it.valor += valor;
      }
    });

    return Array.from(acc.values()).map((x) => ({
      ...x,
      base: Number(x.base || 0),
      valor: Number(x.valor || 0),
    }));
  }, [safeRows, mapIR, mapIVA]);

  const totalRet = useMemo(() => {
    return retenciones.reduce((s, r) => s + Number(r.valor || 0), 0);
  }, [retenciones]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          border: "1px solid #e6e6e6",
          borderRadius: 1.5,
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
              <TableCell sx={{ fontWeight: 700 }}>Retención</TableCell>
              <TableCell sx={{ width: 110, fontWeight: 700 }}>Tipo</TableCell>
              <TableCell sx={{ width: 100, fontWeight: 700 }}>Cod.SRI</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Base</TableCell>
              <TableCell sx={{ width: 90, fontWeight: 700 }}>%</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Valor</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {retenciones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography sx={{ py: 2, color: "#6b7280" }}>
                    No hay retenciones seleccionadas.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              retenciones.map((r, idx) => (
                <TableRow key={`${r.tipo}-${r.codigo_retencion}-${idx}`}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        value={r.descripcion}
                        fullWidth
                        size="small"
                        disabled
                        sx={{
                          "& .Mui-disabled": {
                            backgroundColor: "#fff !important",
                            WebkitTextFillColor: "#111827 !important",
                          },
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ color: "#111827" }}>
                      {r.tipo === "2" ? "IVA" : "Imp. a la Renta"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                  <Typography sx={{ color: "#111827" }}>
                      {r.codigo_retencion}
                  </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={Number(r.base || 0).toFixed(2)}
                      size="small"
                      disabled
                      fullWidth
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{
                        "& .Mui-disabled": {
                          backgroundColor: "#f3f4f6 !important",
                          WebkitTextFillColor: "#111827 !important",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={Number(r.porcentaje || 0).toFixed(2)}
                      size="small"
                      disabled
                      fullWidth
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{
                        "& .Mui-disabled": {
                          backgroundColor: "#f3f4f6 !important",
                          WebkitTextFillColor: "#111827 !important",
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={Number(r.valor || 0).toFixed(2)}
                      size="small"
                      disabled
                      fullWidth
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{
                        "& .Mui-disabled": {
                          backgroundColor: "#f3f4f6 !important",
                          WebkitTextFillColor: "#111827 !important",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}

            {retenciones.length > 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "right", fontWeight: 700 }}>
                  Total retenciones
                </TableCell>
                <TableCell>
                  <TextField
                    value={Number(totalRet || 0).toFixed(2)}
                    size="small"
                    disabled
                    fullWidth
                    inputProps={{ style: { textAlign: "right", fontWeight: 700 } }}
                    sx={{
                      "& .Mui-disabled": {
                        backgroundColor: "#f3f4f6 !important",
                        WebkitTextFillColor: "#111827 !important",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
